import { useWeb3React } from "@web3-react/core";
import { useEffect, useState } from "react";
import { getWalletProviderKind } from "../../config/chains";
import { WalletProviderKind } from "../../enums/walletProvider";
import { LocalStorageKeyEnum } from "../../enums/localStorage";
import { markSelectedWalletForThisTab } from "../../utils/wallet/selectedWalletRecord";
import { coinbaseWallet } from "../../utils/wallet/connectors/coinbaseWallet";
import { eip6963Connector } from "../../utils/wallet/connectors/eip6963";
import { metaMask } from "../../utils/wallet/connectors/metamask";
import { walletConnectV2 } from "../../utils/wallet/connectors/walletConnectV2";
import { getEIP6963Providers } from "../wallet/useEIP6963";
import { WalletEnum } from "../../utils/wallet/types";

const useEagerConnect = () => {
  const { isActive } = useWeb3React();
  const [tried, setTried] = useState(false);

  useEffect(() => {
    if (tried) {
      return;
    }

    const lastConnectedWalletString = localStorage.getItem(LocalStorageKeyEnum.LAST_CONNECTED_WALLET);
    const lastConnectedWallet: WalletEnum | undefined = Number.isNaN(Number(lastConnectedWalletString))
      ? undefined
      : Number(lastConnectedWalletString) as WalletEnum;

    // SDK sessions restore through the SDK provider + <WalletBridge/>, not through
    // web3-react connectors, so skip the eager path for them — otherwise the two
    // race to activate.
    //
    // Gate on the SESSION, not the chain. SDK chains still offer injected wallets
    // in the connect modal (EIP-6963), and picking one persists both
    // LAST_CONNECTED_WALLET and `lastEIP6963Rdns` precisely so it can be restored.
    // Skipping on chain kind alone stranded those sessions: the EIP6963 branch
    // below never ran, `eip6963Connector.provider` stayed undefined across a
    // reload, and getActiveEip1193() fell back to `window.ethereum` — which on that
    // origin has no authorized accounts, so ethers threw "unknown account #0" the
    // moment anything tried to sign.
    const isSdkSession =
      lastConnectedWallet === WalletEnum.PRIVY ||
      lastConnectedWallet === WalletEnum.PARA ||
      lastConnectedWallet === WalletEnum.DOGEOS;

    if (
      getWalletProviderKind() !== WalletProviderKind.WEB3REACT &&
      (lastConnectedWallet === undefined || isSdkSession)
    ) {
      setTried(true);
      return;
    }

    // Connects to last connected connector if valid
    const promises: Promise<any>[] = [];
    switch (lastConnectedWallet) {
      case WalletEnum.METAMASK:
        promises.push(metaMask.connectEagerly?.());
        break;
      case WalletEnum.WALLETCONNECT:
        promises.push(walletConnectV2.connectEagerly?.());
        break;
      case WalletEnum.WALLETLINK:
        promises.push(coinbaseWallet.connectEagerly?.());
        break;
      case WalletEnum.EIP6963: {
        // Find the previously connected EIP-6963 provider by rdns
        const lastRdns = localStorage.getItem(
          LocalStorageKeyEnum.LAST_EIP6963_RDNS
        );
        if (lastRdns) {
          // Arm the refusal for THIS tab. A freshly opened tab has empty
          // sessionStorage, and without this the refusal would be disarmed on
          // exactly the path that motivates it: a wallet uninstalled between
          // sessions, where no announcement matches below, setProvider is never
          // called, and getActiveEip1193 would otherwise go back to signing
          // through whichever extension won window.ethereum.
          markSelectedWalletForThisTab(lastRdns);

          // Wait briefly for EIP-6963 providers to announce themselves
          const tryEagerConnect = () => {
            const providers = getEIP6963Providers();
            const match = providers.find((p) => p.info.rdns === lastRdns);
            if (match) {
              const connector = eip6963Connector;
              (connector as any).setProvider(match.provider);
              return connector.connectEagerly?.() || Promise.resolve();
            }
            return Promise.reject(new Error("EIP-6963 provider not found"));
          };
          // Providers may not be announced yet, retry after a short delay
          promises.push(
            tryEagerConnect().catch(() =>
              new Promise((resolve) => setTimeout(resolve, 500)).then(tryEagerConnect)
            )
          );
        }
        break;
      }
      default:
        break;
    }
    Promise.all(promises).catch(() => {
      setTried(true);
    });
  }, [tried]); // intentionally only running on mount (make sure it's only mounted once :))

  // if the connection worked, wait until we get confirmation of that to flip the flag
  useEffect(() => {
    if (!tried && isActive) {
      setTried(true);
    }
  }, [tried, isActive]);

  return tried;
};
export default useEagerConnect;
